/* The target sheet's „Kalkulator / Wpisz ręcznie" switch and the calculator (mockup
 * `nutrition-designs/web-nutrition.html`, `.mode`, `.src`, `.opt`, `.res`). */

.mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3px);
  padding: var(--space-3px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-11px);
}

.modeButton {
  height: var(--h-seg-option);
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
}

.modeButton[aria-pressed='true'] {
  font-weight: var(--fw-bold);
  color: var(--text);
  background: var(--bg-card);
  box-shadow: var(--shadow-card);
}

.modeButton:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── the client's data ─────────────────────────────────────────────────────── */

.sources {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: var(--space-2);
}

.source {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  padding: var(--space-10px) var(--space-3);
  background: var(--bg-inset);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border);
  border-radius: var(--radius-sm);
}

.sourceMissing {
  border-style: dashed;
  border-color: var(--border-2);
}

.sourceLabel {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

.sourceValue {
  display: flex;
  gap: var(--space-1);
  align-items: baseline;
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-title);
  font-variant-numeric: tabular-nums;
}

.sourceUnit {
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  letter-spacing: normal;
}

.sourceNote {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

.stale {
  font-weight: var(--fw-bold);
  color: var(--warn-ink);
}

.sourceTyped {
  display: flex;
  gap: var(--space-1);
  align-items: center;
}

.sourceInput {
  width: 100%;
  min-width: 0;
  height: 26px;
  padding: var(--space-0) var(--space-6px);
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.sourceInput:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* Marked by a save attempt or a bad value: the edge, the tint and the caption say which input. */
.sourceMissing.sourceDanger {
  background: var(--danger-soft);
  border-style: solid;
  border-color: var(--danger);
}

.sourceDanger .sourceInput {
  border-color: var(--danger);
}

/* `--danger-text-strong`, not `--danger-ink`: the caption sits on the soft danger tint. */
.sourceDanger .sourceNote {
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
}

/* Awaited by a tapped Katch-McArdle: the focus treatment, not an error. */
.sourceMissing.sourceAwaiting {
  background: var(--bg-card);
  border-style: solid;
  border-color: var(--accent);
}

/* Three lines held for the longest caption, so a tapped Katch-McArdle does not move the pills. */
.sourceMissing .sourceNote {
  min-height: 3lh;
}

/* `--accent-ink`: the accent drawn as words. */
.sourceAwaiting .sourceNote {
  font-weight: var(--fw-semibold);
  color: var(--accent-ink);
}

/* ── the choices ───────────────────────────────────────────────────────────── */

.fieldHead {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.fieldHint {
  margin-inline-start: auto;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  text-align: end;
}

.options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
}

.option {
  position: relative;
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
  height: var(--h-seg-option);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--border-2);
  border-radius: var(--r-pill);
}

.option:hover {
  color: var(--text);
}

.optionOn,
.optionOn:hover {
  color: var(--on-ink);
  background: var(--text);
  border-color: transparent;
}

.optionOff,
.optionOff:hover {
  color: var(--text-3);
  border-style: dashed;
}

.option:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* A reserved line: the centred sheet does not grow, so the pills stay under the pointer. */
.choiceMessage {
  min-height: 1lh;
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* The ± field: the option pill's height and border, a value in the middle. */
.precise {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

.stepper {
  display: inline-flex;
  gap: var(--space-1px);
  align-items: center;
  height: var(--h-seg-option);
  padding: var(--space-0) var(--space-2px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.stepper:focus-within {
  border-color: var(--text-3);
}

.stepperButton {
  display: grid;
  flex: none;
  width: 24px;
  height: 24px;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  place-items: center;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--r-circle);
}

.stepperButton:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-0);
}

.stepperButton:hover:not([data-disabled]) {
  background: var(--hover-tint);
}

.stepperButton[data-disabled] {
  color: var(--text-3);
  cursor: not-allowed;
}

.stepperAffix {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* As wide as the range's longest value, so the + button stays under a held pointer. */
.stepperValue {
  box-sizing: content-box;
  width: calc(var(--stepper-chars) * 1ch);
  height: 22px;
  padding: var(--space-0) var(--space-1);
  font-family: var(--font-body);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  text-align: center;
  cursor: text;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
}

.stepperValue:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-0);
}

.stepperValue:hover:not(:focus) {
  background: var(--hover-tint);
}

.preciseReadout {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.preciseWord {
  color: var(--text-3);
}

/* ── the result ────────────────────────────────────────────────────────────── */

.result {
  display: grid;
  grid-template-columns: auto auto auto 1fr;
  gap: var(--space-6px) var(--space-22px);
  align-items: end;
  padding: var(--space-14px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
}

.resultLabel {
  display: block;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: var(--ls-lab);
}

.resultValue {
  font-size: var(--fs-brand);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  letter-spacing: var(--ls-title);
  font-variant-numeric: tabular-nums;
}

.resultBig {
  font-size: var(--fs-state);
}

.resultUnit {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  letter-spacing: normal;
}

.resultMacros {
  display: flex;
  gap: var(--space-3);
  justify-self: end;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.resultMacro {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
}

.resultWhy {
  grid-column: 1 / -1;
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.resultShort {
  grid-column: 1 / -1;
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

.state {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* „Zapisz" with the calculator incomplete: as quiet as a refused confirm, yet a press marks what is
 * missing. Doubled to outrank the ember `.primary` whatever order the modules load in. */
.saveSoft.saveSoft {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);
}

.saveSoft.saveSoft:hover {
  color: var(--text);
}

.useButton {
  height: var(--h-control);
  padding: var(--space-0) var(--space-4);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  border-radius: var(--r-button);
}
